import type { ChatSessionParticipant, MessageVisibilityMode } from "@/api/models"

export type SharedHistoryMarkerPosition = "start" | "end" | "above" | "below"

export type SharedHistoryMarker = {
  id: string
  displayName: string
  accessMode: MessageVisibilityMode
  sharedAt?: string
  position: SharedHistoryMarkerPosition
  messageId?: string
}

export type SharedHistoryMarkerMap = {
  top: SharedHistoryMarker[]
  before: Record<string, SharedHistoryMarker[] | undefined>
  after: Record<string, SharedHistoryMarker[] | undefined>
  bottom: SharedHistoryMarker[]
}

const ensureArray = (map: Record<string, SharedHistoryMarker[] | undefined>, key: string) => {
  if (!map[key]) {
    map[key] = []
  }
  return map[key]
}

export const buildSharedHistoryMarkers = (
  participants: ChatSessionParticipant[] | undefined,
  messageIds: string[],
  currentUserParticipant?: ChatSessionParticipant
): SharedHistoryMarkerMap => {
  const markers: SharedHistoryMarkerMap = { top: [], before: {}, after: {}, bottom: [] }

  if (!participants || participants.length === 0) {
    return markers
  }

  const messageIdSet = new Set(messageIds)
  const currentUserSharedAt = currentUserParticipant?.sharedAt
    ? new Date(currentUserParticipant.sharedAt)
    : undefined
  const hasCurrentUserSharedAt = currentUserSharedAt && !Number.isNaN(currentUserSharedAt.getTime())

  participants.forEach((participant) => {
    if (participant.role === "owner") {
      return
    }
    const markerBase = {
      id: participant.oid,
      displayName: participant.displayName,
      accessMode: participant.messageAccessMode,
      sharedAt: participant.sharedAt,
    }

    const sharedFromMessageId = participant.sharedFromMessageId
    if (participant.messageAccessMode === "full_history") {
      markers.top.push({ ...markerBase, position: "start" })
      return
    }

    if (sharedFromMessageId && messageIdSet.has(sharedFromMessageId)) {
      ensureArray(markers.before, sharedFromMessageId).push({
        ...markerBase,
        position: "start",
        messageId: sharedFromMessageId,
      })
      if (participant.messageAccessMode === "specific_message") {
        ensureArray(markers.after, sharedFromMessageId).push({
          ...markerBase,
          position: "end",
          messageId: sharedFromMessageId,
        })
      }
      return
    }

    if (sharedFromMessageId) {
      const participantSharedAt = participant.sharedAt ? new Date(participant.sharedAt) : undefined
      const hasParticipantSharedAt =
        participantSharedAt && !Number.isNaN(participantSharedAt.getTime())
      const shouldPlaceBelow =
        hasCurrentUserSharedAt &&
        hasParticipantSharedAt &&
        participantSharedAt.getTime() > currentUserSharedAt.getTime()
      const outsidePosition: SharedHistoryMarkerPosition = shouldPlaceBelow ? "below" : "above"
      if (shouldPlaceBelow) {
        markers.bottom.push({ ...markerBase, position: outsidePosition })
      } else {
        markers.top.push({ ...markerBase, position: outsidePosition })
      }
      return
    }

    markers.top.push({ ...markerBase, position: "start" })
  })

  return markers
}
